import { useCallback, useMemo } from "react";
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  type Auth,
  type OAuthProvider,
} from "firebase/auth";
import {
  ensureFirebaseOidcClientConfigured,
  getFirebaseOidcClient,
} from "../firebaseOidcClient";

type FirebaseOidcClient = {
  auth: Auth | null;
  provider: OAuthProvider | null;
  signIn: () => Promise<string>;
  signOut: () => Promise<void>;
  error: string | null;
};

export const useFirebaseOidcClient = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): FirebaseOidcClient => {
  const client = useMemo(() => {
    if (!enabled) {
      return {
        auth: null,
        provider: null,
        error: null,
      };
    }

    ensureFirebaseOidcClientConfigured();
    return getFirebaseOidcClient();
  }, [enabled]);

  const signOut = useCallback(async () => {
    if (!client.auth) {
      return;
    }

    await firebaseSignOut(client.auth);
  }, [client.auth]);

  const signIn = useCallback(async () => {
    if (!client.auth || !client.provider) {
      throw new Error("Firebase OIDC client is not initialized");
    }

    const result = await signInWithPopup(client.auth, client.provider);
    return result.user.getIdToken();
  }, [client.auth, client.provider]);

  return {
    auth: client.auth,
    provider: client.provider,
    signIn,
    signOut,
    error: client.error,
  };
};
